import { Link } from "react-router-dom";

import { Box, Button, IconButton, TableCell, Tooltip } from "@mui/material";
import { includeKeysFromObject } from "../../helper-functions/shared/miscUtils";
import { getPathFromUrl } from "../../helper-functions/shared/pageUtils";

/**
 * Wrapper component for a different buttons.
 *
 * Required props:
 * title
 * path (button with link)
 * icon (icon with callback)
 * onClick (button or icon with callback)
 *
 * Optional props:
 * className
 * variant
 * color
 * size
 * disabled
 * fullWidth
 */
export default function CustomButton(props) {
    const { admin, superuser, title, path, onClick, icon, tooltip, hidden, cell, ...otherProps } = props;

    const superuserDisabled = superuser ? !props.roles?.isSuperuser : false;
    const adminDisabled = admin ? !props.roles?.isAdmin : false;
    const userDisabled = props.disabled;
    const disabled = superuserDisabled || adminDisabled || userDisabled;

    const customButtonProps = includeKeysFromObject(otherProps, ["className", "variant", "color", "size", "fullWidth", "type", "sx", "p", "m"]);
    const prevPathname = getPathFromUrl();

    // React components get discarded during includeObjectsFromConfigByKeys(), define them here.
    const startIcon = props.startIcon;
    const endIcon = props.endIcon;

    // Default props in case of no user-given parameters.
    const defaultButtonProps = {
        className: "small-margin",
        variant: "contained",
        color: "primary",
        size: "medium",
        disabled
    };

    const defaultIconButtonProps = {
        color: "default",
        size: "small",
        disabled
    };

    /**
     * Return the final component.
     */
    const returnComponent = () => {
        if (hidden) return;
        if (!cell) return createComponent();

        return (
            <TableCell colSpan={props.colSpan} align={props.align}>
                {createComponent()}
            </TableCell>
        );
    };

    /**
     * Return the final component according to the props.
     */
    const createComponent = () => {
        if (icon) return iconButton();
        if (!path) return clickButton();
        return linkButton();
    };

    const iconButton = () => {
        if (tooltip) {
            return (
                <Tooltip title={tooltip} placement="bottom" arrow enterTouchDelay={100}>
                    <span>
                        <IconButton {...defaultIconButtonProps} {...customButtonProps} onClick={onClick}>
                            {icon}
                        </IconButton>
                    </span>
                </Tooltip>
            );
        }

        return (
            <IconButton {...defaultIconButtonProps} {...customButtonProps} onClick={onClick}>
                {icon}
            </IconButton>
        );
    };

    const clickButton = () => {
        if (tooltip) {
            return (
                <Tooltip title={tooltip} placement="bottom" arrow enterTouchDelay={100}>
                    <Box alignContent="center">
                        <Button {...defaultButtonProps} {...customButtonProps} onClick={onClick} startIcon={startIcon} endIcon={endIcon}>
                            {title}
                        </Button>
                    </Box>
                </Tooltip>
            );
        }

        return (
            <Button {...defaultButtonProps} {...customButtonProps} onClick={onClick} startIcon={startIcon} endIcon={endIcon}>
                {title}
            </Button>
        );
    };

    const linkButton = () => {
        if (props.disabled) {
            return (
                <Button {...defaultButtonProps} {...customButtonProps} disabled startIcon={startIcon} endIcon={endIcon}>
                    {title}
                </Button>
            );
        }

        return (
            <Link to={path} state={{ prevPathname }} className="text-link">
                <Button {...defaultButtonProps} {...customButtonProps} startIcon={startIcon} endIcon={endIcon}>
                    {title}
                </Button>
            </Link>
        );
    };

    return returnComponent();
}
